import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Heart, Sparkles, Users, X } from 'lucide-react'
import { RedeemUsers } from '../../../../Data/user.dto'
import DefaultImagePNG from '../../../../assets/images/DefaultProfilePic.png'
import { IMAGE_CDN } from '../../../../config/config'
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from '../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles'

interface ComponentProps {
  redeemUser: RedeemUsers[]
  closeModal: () => void
  showFunction: (id: number) => void
}

const shouldShowRedeemUser = (user: RedeemUsers) => {
  const hasName = Boolean(user.name?.trim())
  const hasEmail = Boolean(user.email?.trim())
  return hasName || hasEmail
}

const SelectRedeemUserListPopup: React.FC<ComponentProps> = ({
  redeemUser,
  closeModal,
  showFunction,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<number>()

  const filteredRedeemUsers = useMemo(
    () => redeemUser.filter(shouldShowRedeemUser),
    [redeemUser]
  )

  useEffect(() => {
    if (filteredRedeemUsers.length === 0) {
      setSelectedUserId(undefined)
      return
    }
    const authorized = filteredRedeemUsers.find((u) => u.is_authorized === 'True')
    setSelectedUserId(authorized?.id ?? filteredRedeemUsers[0].id)
  }, [filteredRedeemUsers])

  const handleSubmit = () => {
    if (selectedUserId) {
      showFunction(selectedUserId)
      closeModal()
    }
  }

  return createPortal(
    <ModalOverlay onClick={closeModal}>
      <Shell onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={10} />
                Liked images
              </HeroBadge>
              <ModalTitle>Select redeem user</ModalTitle>
              <ModalSubtitle>
                Choose a user to view photos they liked in this folder.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={closeModal} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <Body>
          <MetaRow>
            <CountPill>
              <Users size={13} />
              {filteredRedeemUsers.length} user{filteredRedeemUsers.length === 1 ? '' : 's'}
            </CountPill>
          </MetaRow>

          {filteredRedeemUsers.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Heart size={22} />
              </EmptyIcon>
              <EmptyTitle>No redeem users yet</EmptyTitle>
              <EmptyHint>
                When someone redeems an album code, they will appear here so you can view their liked photos.
              </EmptyHint>
            </EmptyState>
          ) : (
            <UserList>
              {filteredRedeemUsers.map((user) => {
                const isSelected = selectedUserId === user.id
                return (
                  <UserRow
                    key={user.id}
                    type="button"
                    $selected={isSelected}
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <Avatar
                      src={
                        user.profile_image
                          ? `${IMAGE_CDN}/${user.profile_image}`
                          : DefaultImagePNG
                      }
                      alt={user.name ? `${user.name} profile` : 'User profile'}
                    />
                    <UserInfo>
                      <UserName>{user.name || user.email || 'Unknown user'}</UserName>
                      <UserMeta>
                        {user.email ||
                          `${user.country_code ?? ''} ${user.phone_number ?? ''}`.trim() ||
                          'No contact info'}
                      </UserMeta>
                    </UserInfo>
                    <RadioDot $selected={isSelected} />
                  </UserRow>
                )
              })}
            </UserList>
          )}
        </Body>

        <Footer>
          <CancelBtn type="button" onClick={closeModal}>
            Cancel
          </CancelBtn>
          <SubmitBtn
            type="button"
            disabled={!selectedUserId || filteredRedeemUsers.length === 0}
            onClick={handleSubmit}
          >
            View liked images
          </SubmitBtn>
        </Footer>
      </Shell>
    </ModalOverlay>,
    document.body
  )
}

export default SelectRedeemUserListPopup

const Shell = styled(ModalShell)`
  max-width: 520px;
`

const Body = styled(ModalBody)`
  padding-top: 16px;
  gap: 14px;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
`

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 2px;
`

const UserRow = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid
    ${(p) => (p.$selected ? 'rgba(201, 77, 170, 0.35)' : 'rgba(23, 23, 23, 0.08)')};
  background: ${(p) => (p.$selected ? 'rgba(201, 77, 170, 0.08)' : '#fafafa')};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.28);
    background: rgba(201, 77, 170, 0.06);
    transform: translateY(-1px);
  }
`

const Avatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
`

const UserInfo = styled.div`
  min-width: 0;
  flex: 1;
`

const UserName = styled.p`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const UserMeta = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.52);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RadioDot = styled.span<{ $selected?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${(p) => (p.$selected ? 'transparent' : 'rgba(23, 23, 23, 0.15)')};
  background: ${(p) =>
    p.$selected
      ? 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'
      : '#fff'};
  flex-shrink: 0;
  box-shadow: ${(p) => (p.$selected ? '0 2px 8px rgba(201, 77, 170, 0.25)' : 'none')};
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 36px 20px;
  border-radius: 16px;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  background: #fafafa;
  text-align: center;
`

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
`

const EmptyTitle = styled.p`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`

const EmptyHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(23, 23, 23, 0.52);
  max-width: 320px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px 24px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
`

const CancelBtn = styled.button`
  flex: 1;
  height: 46px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`

const SubmitBtn = styled(PrimaryBtn)`
  flex: 1;
  width: auto;
`
